<i18n>
{
  "de": {
    "reply": "Antworten",
    "pin": "Kommentar anpinnen",
    "unpin": "Kommentar lösen",
    "confirmDeletion": "Bitte bestätige das Löschen",
    "attentionDelete": "Achtung! Du bist kurz davor den Kommentar zu löschen von",
    "cancel": "Abbrechen",
    "deleteComment": "Kommentar löschen",
    "wrote": "schrieb"

  },
  "en": {
    "reply": "Reply",
    "pin": "Pin comment",
    "unpin": "Unpin comment",
    "confirmDeletion": "Please confirm deletion",
    "attentionDelete": "Attention! You are about to delete the comment by",
    "cancel": "Cancel",
    "deleteComment": "Delete comment",
    "wrote": "wrote"
  }
}
</i18n>

<template>
  <article
    :id="`comment_${cmt._id}`"
    :ref="`comment_${comment._id}`"
    class="media post-community box mb-0"
    :class="{
      'pl-10 pr-10': level === 0,
      'pl-40 pr-10': level === 1 && displayContext === 'isolatedView',
      'pl-40 pr-0': level === 1 && displayContext !== 'isolatedView',
      'pl-0 pr-10': level > 1 && displayContext === 'isolatedView',
      'pl-0 pr-0': level > 1 && displayContext !== 'isolatedView',
      'gradient-border': pinned
    }"
  >
    <div class="media-content post-comment">
      <div class="content is-size-6">
        <div class="comment-header">
          <figure class="avatar-figure">
            <img
              v-if="cmt.user && cmt.avatarMediaId"
              :src="`/services/media/image/${cmt.user}/${cmt.avatarMediaId}/96/0/image.webp`"
            >
            <img
              v-else-if="cmt.avatar && cmt.avatar !== ''"
              :src="avatarImage"
            >
            <user-icon
              v-else
              class="dummy-avatar-icon"
            />
          </figure>
          <div class="comment-meta">
            <strong class="has-text-primary">{{ cmt.nickname }}</strong>
            <small class="ml-10 comment-meta-date">{{ formatDate(cmt.timestamp) }}</small>
          </div>
        </div>
        <div
          v-if="parent"
          class="pl-43"
        >
          <div class="quote-text">
            <a
              href="#"
              class="quote-link"
              @click.prevent="scrollToComment(parent._id)"
            >
              {{ parent.nickname }} {{ $t('wrote') }}: &ldquo;{{ truncatedParentContent }}&rdquo;
            </a>
          </div>
        </div>
        <div
          class="mt-5 comment-content pl-43"
          v-html="cmt.content"
        />
        <div class="comments pl-42">
          <div
            v-if="isAuthenticated"
            ref="commentControls"
            class="comment-controls"
          >
            <div
              v-if="!markDeleted"
              class="cc-row"
            >
              <a @click="addEditor()">
                <span class="small is-relative">
                  <i class="material-icons icon-comment">mode_comment</i>
                  {{ $t('reply') }}
                </span>
              </a>
              <div class="cc-row-right">
                <!-- <a @click="likeComment(comment._id)"><i class="material-icons icon-heart">favorite_border</i></a> -->
                <div v-if="$store.state.user.id === $store.state.artist._id">
                  <span v-if="!cmt.parent">
                    <a
                      v-if="cmt.pinned"
                      :title="$t('unpin')"
                      @click="setPinned(false)"
                    >
                      <i class="small material-icons">star</i>
                    </a>
                    <a
                      v-else
                      :title="$t('pin')"
                      @click="setPinned(true)"
                    >
                      <i class="small material-icons">star_outline</i>
                    </a>
                  </span>
                  <a @click="showDeleteModal = true">
                    <i class="small material-icons">delete</i>
                  </a>
                </div>
              </div>
            </div>
          </div>
          <no-ssr placeholder="Loading...">
            <portal-target :name="`post_editor_${cmt._id}`" />
          </no-ssr>
        </div>
      </div>
    </div>
    <div
      v-if="children.length"
      class="post-comment-answers"
    >
      <div v-if="children.length">
        <post-comment
          v-for="(childComment, idx) in children"
          :key="generateKey(`${idx}_${childComment._id}`)"
          :comment="childComment"
          :index="idx"
          :community="false"
          :level="level + 1"
          :parent="comment"
        />
      </div>
    </div>
    <deleteModal
      v-if="showDeleteModal"
      defaultHeader="true"
      defaultFooter="true"
      @close="showDeleteModal = false"
    >
      <div slot="header">
        {{ $t('confirmDeletion') }}
      </div>
      <div slot="body">
        <p>{{ $t('attentionDelete') }} {{ cmt.nickname }}:</p>
        <div
          class="quote"
          v-html="cmt.content"
        />
      </div>
      <div slot="footer">
        <button
          type="button"
          class="button is-outlined is-medium cancel-button"
          @click="showDeleteModal = false"
        >
          {{
            $t('cancel')
          }}
        </button>
        <button
          type="button"
          class="button is-primary is-outlined is-medium delete-button"
          @click="confirmDelete"
        >
          {{
            $t('deleteComment') }}
        </button>
      </div>
    </deleteModal>
  </article>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import deleteModal from '~/components/modal'
import userIcon from '~/components/user-icon'

export default {
  name: 'comments',
  props: ['comment', 'parent', 'level', 'pinned', 'displayContext'],
  inject: ['moveEditor', 'deleteComment'],

  components: {
    postComment: () => import('~/components/post-comment'),
    deleteModal,
    userIcon
  },

  data() {
    return {
      showDeleteModal: false,
      markDeleted: false,
      observer: null
    }
  },

  computed: {
    ...mapState(['isAuthenticated', 'markedAsReadComments', 'markedAsSentComments']),
    children() {
      return this.cmt.children || []
    },
    avatarImage() {
      if (typeof this.cmt.avatar === 'string') {
        return this.cmt.avatar
      }
      const availableSizes = this.cmt.avatar.availableSizes
      const image = availableSizes.find((size) => size.name === 'avatar-s')
      if (image) {
        return image.path
      }
      return ''
    },
    truncatedParentContent() {
      if (!this.parent) {
        return ''
      }
      return this.parent.contentShort
    },
    cmt() {
      return this.markDeleted
        ? Object.assign({}, this.comment, {
          avatar: '',
          content: '<p style="font-style: italic;">This comment has been removed.</p>',
          nickname: 'Deleted Comment'
        })
        : Object.assign({}, this.comment, {
          ancestors: this.comment.ancestors || []
        })
    }
  },

  methods: {
    ...mapActions(['markAsReadComment']),
    generateKey(id) {
      return `${this.displayContext}_${id}`
    },
    async addEditor() {
      this.moveEditor(this.cmt)
    },
    confirmDelete() {
      this.deleteComment(this.comment)
      this.markDeleted = true
      this.showDeleteModal = false
    },
    setPinned(enabled) {
      return this.$emit('pinned', { enabled, comment: this.cmt })
    },
    scrollToComment(id) {
      const element = document.getElementById(`comment_${id}`)
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' })
      }
    },
    initObserver() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              this.queueMarkAsRead()
            }
          })
        },
        { threshold: 0.5 }
      )
      this.observer.observe(this.$refs[`comment_${this.comment._id}`])
    },
    queueMarkAsRead() {
      if (
        !this.markedAsReadComments.includes(this.comment._id) &&
        !this.markedAsSentComments.includes(this.comment._id)
      ) {
        this.markAsReadComment(this.comment._id)
      }
    }
  },
  created() {
    if (this.comment.status === 'deleted') {
      this.markDeleted = true
    }
  },
  mounted() {
    this.initObserver()
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
    }
  }
}
</script>

<style lang="scss">
@import "assets/sass/_variables";

.post-comment-answers {
  width: 100% !important;
  padding-bottom: 0px;
}

.post-community {
  display: flex;
  flex-direction: column;
  margin-top: 0px !important;
  margin-bottom: 0;

  &.box {
    padding-bottom: 0;

    &.gradient-border {
      -webkit-box-shadow: 0px 0px 16px -1px rgba(204, 204, 204, 1);
      -moz-box-shadow: 0px 0px 16px -1px rgba(204, 204, 204, 1);
      box-shadow: 0px 0px 16px -1px rgba(204, 204, 204, 1);
    }
  }

  .media-left {
    .is-32h {
      height: 32px;
      width: auto;
    }
  }

  .media-content {
    width: 100%;
    font-size: 0.9rem;
    margin-top: 0 !important;

    &.post-comment {
      overflow: initial !important;
      padding-bottom: 25px !important;
      margin-bottom: 0px !important;

      .content {
        .comment-content {
          word-break: break-word;
          p {
            margin-block: 0;
            margin-inline: 0;
          }
        }
      }
    }

    .comment-header {
      display: flex;
      align-items: flex-start;

      .avatar-figure {
        margin-right: 10px;
        width: 32px;
        height: 32px;
        flex-shrink: 0;
        margin-bottom: 10px;
      }

      .comment-meta {
        display: flex;
        align-items: center;
        justify-content: space-between;
        width: 100%;

        strong {
          margin-right: auto;
        }

        small {
          margin-left: 10px;
        }

        .comment-meta-date {
          font-size: 0.8rem;
        }
      }
    }

    .comments {
      margin-top: 10px;

      .comment-controls {
        display: flex;
        flex-flow: row nowrap;
        justify-content: space-between;

        &.unauthorized {
          position: relative !important;
          display: inline-block;
          font-size: 0.7rem !important;
        }

        .cc-row {
          display: flex;
          justify-content: space-between;
          width: 100%;

          .cc-row-right {
            display: flex;
            width: auto;
            flex-flow: row nowrap;
            justify-content: space-between;
          }
        }
      }

      // .dummy-avatar-icon {
      //   color: color-mix(in hsl, var(--tiles-background-color), var(--tiles-background-text-color) 20%);
      // }
    }
  }

  .avatar-figure {
    img {
      border-radius: 50%;
      width: 32px;
      height: 32px;
      object-fit: cover;
    }
  }

  .small {
    font-size: 0.8rem;
    padding-left: 30px;
    top: 7px;

    .material-icons {
      &.icon-comment {
        font-size: 1.5em;
      }
    }
  }

  .hidden {
    display: none !important;
  }

  .material-icons {
    padding: 10px 0 10px 10px;
    font-size: 20px;

    &.icon-comment {
      padding-left: 0;
      padding-right: 6px;
      position: absolute;
      top: -12px;
      left: 0;
    }
  }

  .modal-header {
    background-color: var(--primary-color);
    color: $background;
  }

  .modal-body .quote {
    margin-top: 20px;
    text-indent: 2em;
    border-left: 8px solid #ccc;
    background-color: #f9f9f9;
  }

  .modal-footer {
    display: flex;
    flex-flow: row nowrap;
    align-items: flex-end;
  }
}

.post-media .box {
  margin: 10px 0 2px 2px;
  width: 99%;
}

.item-meta {
  font-size: 0.9em;
}

.audio-item {
  display: flex;
  flex-flow: column nowrap;
  margin-bottom: 10px;

  audio {
    width: 100%;
  }
}

.pdf-item {
  display: flex;
  flex-direction: row;
  flex-wrap: nowrap;
  background-color: $gt-blue-light;
  color: var(--secondary-color);
  padding: 10px;
  margin-bottom: 10px;

  i {
    margin-right: 8px;
  }
}

.quote-link {
  font-size: 0.8rem;
  color: var(--primary-color);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
}

@media only screen and (max-width: $mobile) and (min-width: 580px) {
  .post-media {
    .textual {
      display: flex;
      flex-flow: row wrap;
      justify-content: space-around;
    }

    .non-textual {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      justify-content: space-between;

      .item {
        width: 50%;
        padding: 0.25rem;

        figure {
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          min-height: 200px;
          font-size: 0.9em;
        }
      }
    }
  }
}
</style>
